import { getGetFrontRichiesteIdDocumentoConsolidatoQueryOptions } from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import {
  useAuthDownload,
  useHasDocumentsInlineViewer,
} from '@framework/common-web';
import { useTranslation } from '@framework/i18n';
import { Button, message } from 'antd';
import { useCallback } from 'react';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { useTheme } from 'styled-components';
import { useButtonResponsiveProps } from './useButtonResponsiveProps';

interface Props {
  submissionId: string;
  /** Permette di forzare la visualizzazione desktop */
  ignoreMobile?: boolean;
  /** Mostra sempre l'icona */
  forceIcon?: boolean;
}

/**
 * Scarica (o visualizza) il PDF consolidato dell'intera richiesta, ovvero un
 * unico documento contenente tutti i moduli.
 */
export function DownloadConsolidatedSubmissionButton(props: Props) {
  const { submissionId, forceIcon, ignoreMobile } = props;
  const { t } = useTranslation();
  const theme = useTheme();

  const hasInlineViewer = useHasDocumentsInlineViewer();

  const { download, isFetching } = useAuthDownload(
    getGetFrontRichiesteIdDocumentoConsolidatoQueryOptions,
    [submissionId],
    {
      onError: e => {
        console.error(`[DownloadConsolidatedSubmissionButton] Error: `, { error: e }); // prettier-ignore
        message.error(getNetworkErrorMessage(e, t("submission-consolidated-pdf-button.errore-in-download",'Si è verificato un problema durante il download del file'))); // prettier-ignore
      },
      showInlineViewer: hasInlineViewer,
      openInNewWindow: theme.openModulePdfInNewWindow,
    },
  );

  const isMobile = useBreakpoint(down('md'));
  const buttonText = hasInlineViewer
    ? t(
        'submission-consolidated-pdf-button.visualizza-pdf-richiesta',
        'Visualizza PDF Richiesta',
      )
    : t(
        'submission-consolidated-pdf-button.scarica-pdf-richiesta',
        'Scarica PDF Richiesta',
      );

  const buttonResponsiveProps = useButtonResponsiveProps({
    buttonText,
    forceIcon,
    hasInlineViewer,
    ignoreMobile,
    isMobile,
  });

  const handleClick = useCallback(() => {
    if (submissionId) {
      download();
    }
  }, [download, submissionId]);

  return (
    <Button
      {...buttonResponsiveProps}
      onClick={handleClick}
      disabled={!submissionId}
      loading={isFetching}
      size="large"
      type="default"
    />
  );
}
